import {useEffect, useState} from 'react';
import dayjs from 'dayjs';
import {capitalize} from 'lodash';

import EmailIcon from '@app/assets/icons/mail-box.svg?react';
import IPhoneIcon from '@app/assets/icons/iphone.svg?react';
import UserIcon from '@app/assets/icons/user.svg?react';
import CalendarCheckIcon from '@app/assets/icons/calendar-check.svg?react';
import CalendarCancelIcon from '@app/assets/icons/calendar-cancel.svg?react';
import CalendarIcon from '@app/assets/icons/calendar-01.svg?react';

import {
  AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE, AUTOMATION_SCHEDULE_STATUS_VALUE, AUTOMATION_SCHEDULE_TYPE_VALUE,
  AutomationReviewRequestInterface,
  AutomationReviewRequestStatusType, AutomationScheduleInterface,
} from '@app/models/automations.ts';
import {useQuery} from "@tanstack/react-query";
import {getAutomationReviewRequests} from "@app/services/automations.ts";
import {Loading, Pagination} from "@app/components";
import {toast} from "react-toastify";

interface AutomationReviewRequestsTableProps {
  automationId?: string;
}

const getStatusColor = (status: AutomationReviewRequestStatusType) => {
  switch (status) {
    case AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.STARTED:
      return 'bg-brand-baseBlue';
    case AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.COMPLETED:
      return 'bg-system-success';
    case AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.CANCELLED:
      return 'bg-system-error';
    case AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.SKIPPED:
      return 'bg-system-warning';
    default:
      return 'bg-base-antiFlash';
  }
};

export const AutomationReviewRequestsTable = ({
  automationId,
}: AutomationReviewRequestsTableProps) => {
  const [activeTab, setActiveTab] = useState<AutomationReviewRequestStatusType|null>(null);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const {
    data: reviewRequestsData,
    isLoading: isLoading,
    // refetch: refetchSchedules,
    error: reviewRequestsError,
    isError: hasError,
  } = useQuery({ //
    queryKey: ['automation-reviewRequests', automationId, activeTab, page, limit],
    queryFn: async () => {
      console.log('queryFn', automationId, page, limit, activeTab);
      return getAutomationReviewRequests(automationId as string, page, limit, activeTab ?? undefined);
    },
    enabled: true,
  });

  useEffect(() => {
    if (hasError && reviewRequestsError) {
      toast.error(reviewRequestsError.message || 'Failed to load reviewRequests');
    }
  }, [hasError, reviewRequestsError]);


  const handleActiveTab = (tab: AutomationReviewRequestStatusType|null) => {
    if (activeTab === tab) {
      setPage(page + 1);
      return;
    }

    setActiveTab(tab);
    setPage(1);
    // refetchSchedules();
  };

  const handleLimitChange = (newLimit: number | undefined) => {
    setLimit(newLimit ?? 10);
    setPage(1);
  };

  const handleNextPage = () => {
    setPage((prev) => prev + 1);
  };

  const handlePreviousPage = () => {
    setPage((prev) => Math.max(prev - 1, 1));
  };

  return (
    <div className="automation-shecedules-table" data-automation-id={automationId}>

      <div className="flex gap-2 items-stretch justify-stretch">
        <div className={`p-2 rounded-t w-1/2 border-b-4 ${activeTab === null ? 'bg-white border-base-raisinBlack' : 'bg-base-ghostWhite border-transparent cursor-pointer'}`} onClick={() => handleActiveTab(null)}>
          <div className="text-3xl font-bold">
            { reviewRequestsData?.data?.countStarted ?? 0}
          </div>
          <div className="text-base-coolGray text-sm">
            Started
          </div>
        </div>
        <div className={`p-2 rounded-t w-1/2 border-b-4 ${activeTab === AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.COMPLETED ? 'bg-white border-base-raisinBlack' : 'bg-base-ghostWhite border-transparent cursor-pointer'}`} onClick={() => handleActiveTab(AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.COMPLETED)}>
          <div className="text-3xl font-bold">
            { reviewRequestsData?.data?.countCompleted ?? 0 }
          </div>
          <div className="text-base-coolGray text-sm">
            Completed
          </div>
          <div className="text-brand-dark3 text-sm">
            { (reviewRequestsData?.data?.countCompleted > 0 && reviewRequestsData?.data?.countStarted > 0) ? Math.round(reviewRequestsData?.data?.countCompleted / reviewRequestsData?.data?.countStarted * 100) : 0}%
          </div>
        </div>
      </div>

      <div className="bg-white p-2 md:p-4 h-fit w-full rounded-b shadow-sm">
        {/*<h5 className="font-bold text-sm mb-2">Schedules</h5>*/}
        {/*<hr className="border-gray-200 my-3"/>*/}

        {isLoading ? (
          <div className="flex items-center justify-center min-h-[400px]">
            <Loading />
          </div>
        ) : (
          reviewRequestsData?.data?.reviewRequests?.length === 0 ? (
            <p className="text-base-coolGray text-sm">No reviewRequests</p>
          ) : (
            <>
              <div className="overflow-x-auto -mx-4">
                <table className="w-full text-sm min-w-[320px]">
                  <thead>
                  <tr>
                    <th className="p-4 text-xs font-medium text-left text-base-coolGray uppercase" >
                      Name
                    </th>
                    <th className="p-4 text-xs font-medium text-left text-base-coolGray uppercase" >
                      Email
                    </th>
                    <th className="p-4 text-xs font-medium text-left text-base-coolGray uppercase" >
                      Date
                    </th>
                    <th className="p-4 text-xs font-medium text-left text-base-coolGray uppercase" >
                      Status
                    </th>
                  </tr>
                  </thead>
                  <tbody>
                  {reviewRequestsData?.data?.reviewRequests?.map((reviewRequest: AutomationReviewRequestInterface, index: number) => {

                    return <tr key={reviewRequest.id} className={index !== reviewRequestsData?.data?.reviewRequests?.length - 1 ? "border-b border-gray-200" : ""}>
                      <td className="p-4 min-w-fit w-0 align-middle">
                        <div className="w-full flex items-center justify-start gap-2">
                          <span className="bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">
                            <UserIcon className="w-4 h-4 [&_path]:stroke-base-coolGray"  />
                          </span>
                          <span>{reviewRequest.customerName}</span>
                          {/*status*/}
                          {/*type*/}
                          {/*customerName*/}
                          {/*customerEmail*/}
                          {/*customerPhone*/}
                        </div>
                      </td>
                      <td className="p-4 min-w-fit w-0 align-middle">
                        <div className="w-full flex items-center justify-start gap-2">
                          <span className="bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">
                            <EmailIcon className="w-5 h-5" />
                          </span>
                          <span>{reviewRequest.customerEmail}</span>
                        </div>
                        {reviewRequest.customerPhone && (

                          <div className="w-full flex items-center justify-start gap-2">
                            <span className="bg-base-antiFlash p-1 rounded-sm inline-flex items-center justify-center">
                              <IPhoneIcon className="w-5 h-5" />
                            </span>
                            <span>{reviewRequest.customerPhone}</span>
                          </div>
                        )}
                      </td>
                      <td className="p-4 min-w-fit w-0 align-middle">
                        {/*<div className="w-full flex items-center justify-start gap-2">*/}
                        {/*  <div className={`p-1 rounded-sm flex items-center justify-center ${(reviewRequest.status === AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.COMPLETED ? 'bg-system-successLight' : 'bg-base-antiFlash')}`}>*/}
                        {/*    {(reviewRequest.status === AUTOMATION_REVIEW_REQUESTS_STATUS_VALUE.COMPLETED ? <CalendarCheckIcon className="w-5 h-5"/> : <CalendarIcon className="w-5 h-5"/>)}*/}
                        {/*  </div>*/}
                        {/*  <span className="text-nowrap">{reviewRequestDate.format('MMM DD, YYYY HH:mm')}</span>*/}
                        {/*  /!*{((schedule.type === SCHEDULE_TYPE_VALUE.INITIAL) && <span title="Initial">*!/*/}
                        {/*  /!*  <InfoIcon className="w-4 h-4 text-brand-baseBlue ml-2" />*!/*/}
                        {/*  /!*</span>)}*!/*/}
                        {/*</div>*/}


                        {reviewRequest.schedules?.map((schedule: AutomationScheduleInterface) => {
                          return (
                              <div key={schedule.id} className="flex items-center gap-2 my-1">
                                <div className={`p-1 rounded-sm flex items-center justify-center ${schedule.status === AUTOMATION_SCHEDULE_STATUS_VALUE.COMPLETED ? 'bg-green-100' : 'bg-gray-100'}`}>
                                  {schedule.status === AUTOMATION_SCHEDULE_STATUS_VALUE.COMPLETED ? (
                                      <CalendarCheckIcon className="w-4 h-4"/>
                                  ) : schedule.status === AUTOMATION_SCHEDULE_STATUS_VALUE.SKIPPED ? (
                                      <CalendarCancelIcon className="w-4 h-4 [&_path]:stroke-system-error" />
                                  ) : (
                                      <CalendarIcon className="w-4 h-4" />
                                  )}
                                </div>
                                <span className="text-sm text-gray-700 whitespace-nowrap">
                                    {schedule.type === AUTOMATION_SCHEDULE_TYPE_VALUE.INITIAL ? 'Initial send:' : (schedule.status === AUTOMATION_SCHEDULE_STATUS_VALUE.COMPLETED ? 'Sent on:' : 'Send again:')}
                                  </span>
                                <span className="text-sm text-gray-700 whitespace-nowrap">
                                    {dayjs(schedule.sendDate).format('MMM DD, YYYY HH:mm')}
                                  </span>
                              </div>
                          );
                        })}
                      </td>
                      <td className="p-4 min-w-fit w-0 align-middle">
                        <div className="w-full flex items-center justify-start gap-1">
                          <span className={`w-2 h-2 inline-block rounded-full ${getStatusColor(reviewRequest.status)}`}></span>
                          <span>{capitalize(reviewRequest.status ?? '')}</span>
                          {/*<span>{reviewRequest.status}</span> {reviewRequest.type}*/}
                        </div>
                      </td>
                    </tr>
                  })}
                  </tbody>
                </table>
              </div>
              <hr className="border-gray-200 mb-2" />
              {/*<pre style={{ border: 'solid 1px red', textAlign: 'left' }}>{JSON.stringify(requestData?.data, null, 4)}</pre>*/}
              {/*<pre style={{ border: 'solid 1px green', textAlign: 'left' }}>{JSON.stringify({ page, limit }, null, 4)}</pre>*/}
              {reviewRequestsData?.data?.pages && (
                <Pagination handleLimitChange={handleLimitChange} handleNextPage={handleNextPage} handlePrevPage={handlePreviousPage} page={page} total={reviewRequestsData?.data?.total} />
              )}
            </>
          )
        )}

      </div>

    </div>
  );
};
